import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { Flex, Text, TextInput, RatingDots, Heading } from '@televet/kibble-ui';
import WildcardTextInput, {
  WildcardInputQuestionTypes,
} from 'pages/Forms/FormsContent/FormsDrafting/WildcardInputs/WildcardTextInput';
import WildCardButton from 'shared/components/WildCardButton';
import SurveyComposeQuestion from './SurveyComposeQuestion';
import StatusChangeSelect from './StatusChangeSelect';
import { ISurveyComposeSettings, useSurveySettingsContext } from 'pages/Survey/context/SurveySettingsContext';
import { SurveySettingsActions } from '../../context/SurveySettingsReducer';
import { SurveyType } from 'shared/types/graphql';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SurveyEvents } from 'shared/providers/TrackingProvider/EventNames';

const SurveyCompose = (): JSX.Element => {
  const { dispatch, composeSettings, surveyType } = useSurveySettingsContext();
  const [showPreview, setShowPreview] = useState<boolean | undefined>(true);
  const introMessageRef = useRef<HTMLTextAreaElement | null>(null);
  const ratingQuestionRef = useRef<HTMLTextAreaElement | null>(null);
  const positiveQuestionRef = useRef<HTMLInputElement | null>(null);
  const negativeQuestionRef = useRef<HTMLInputElement | null>(null);
  const { trackEvent } = useTracking();

  const isNpsSurvey = useMemo(() => surveyType === SurveyType.Nps, [surveyType]);

  const handleChange = useCallback(
    (field: keyof Omit<ISurveyComposeSettings, 'sentStatusId'>, newValue: string, callback?: () => void) => {
      dispatch({
        type: SurveySettingsActions.UPDATE_COMPOSE_SETTINGS,
        payload: { ...composeSettings, [field]: newValue },
      });

      switch (field) {
        case 'surveyName':
        case 'introMessage':
          dispatch({ type: SurveySettingsActions.SET_RATING_WORKFLOW_CHANGED, payload: true });
          dispatch({ type: SurveySettingsActions.SET_POSITIVE_WORKFLOW_CHANGED, payload: true });
          dispatch({ type: SurveySettingsActions.SET_NEGATIVE_WORKFLOW_CHANGED, payload: true });
          break;
        case 'ratingQuestion':
          dispatch({ type: SurveySettingsActions.SET_RATING_WORKFLOW_CHANGED, payload: true });
          break;
        case 'positiveQuestion':
        case 'positiveStatusId':
          dispatch({ type: SurveySettingsActions.SET_POSITIVE_WORKFLOW_CHANGED, payload: true });
          break;
        case 'negativeQuestion':
        case 'negativeStatusId':
          dispatch({ type: SurveySettingsActions.SET_NEGATIVE_WORKFLOW_CHANGED, payload: true });
          break;
      }

      trackEvent(SurveyEvents.SurveySettingsUpdated, { field, message: newValue });

      if (callback) callback();
    },
    [composeSettings, dispatch, trackEvent],
  );

  const handleSurveyNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    handleChange('surveyName', newValue);
  };

  const handleIntroMessageChange = useCallback(
    (newValue: string) => {
      handleChange('introMessage', newValue);
    },
    [handleChange],
  );

  const handleAddWildCard = (newValue: string): void => {
    const callback = (): void => setShowPreview(false);
    handleChange('introMessage', newValue, callback);
  };

  const handleRatingQuestionChange = useCallback(
    (newValue: string) => {
      handleChange('ratingQuestion', newValue);
    },
    [handleChange],
  );

  const handlePositiveQuestionChange = useCallback(
    (newValue: string) => {
      handleChange('positiveQuestion', newValue);
    },
    [handleChange],
  );

  const handlePositiveStatusChange = useCallback(
    (newValue: string) => {
      handleChange('positiveStatusId', newValue);
    },
    [handleChange],
  );

  const handleNegativeQuestionChange = useCallback(
    (newValue: string) => {
      handleChange('negativeQuestion', newValue);
    },
    [handleChange],
  );

  const handleNegativeStatusChange = useCallback(
    (newValue: string) => {
      handleChange('negativeStatusId', newValue);
    },
    [handleChange],
  );

  const disallowedWildcards = Object.values(WildcardValues).filter(
    (value) => ![WildcardValues.Time, WildcardValues.ClinicName, WildcardValues.PetName].includes(value),
  );

  const validate = useCallback(
    (value: string) => {
      return !!value && !disallowedWildcards.some((wildcard) => value.includes(wildcard));
    },
    [disallowedWildcards],
  );

  const getErrorMessage = useCallback(
    (value: string) => {
      const invalidWildcard = disallowedWildcards.find((wildcard) => value.includes(wildcard));
      if (!value) {
        return 'This field cannot be empty.';
      } else if (invalidWildcard) {
        return `Invalid wildcard: ${invalidWildcard}. This wildcard will not be evaluated in the message to the client.`;
      }
      return 'Invalid value.';
    },
    [disallowedWildcards],
  );

  const RatingQuestionTitle = (): JSX.Element => <Text>1. {surveyType} Rating</Text>;

  const PositiveQuestionTitle = (): JSX.Element =>
    isNpsSurvey ? (
      <Text>
        2. Followup for <b>Promoters (9-10)</b> and <b>Passives (7-8)</b>
      </Text>
    ) : (
      <Text>
        2. Followup question for <b>Satisfied (3-5)</b>
      </Text>
    );

  const NegativeQuestionTitle = (): JSX.Element =>
    isNpsSurvey ? (
      <Text>
        2. Followup for <b>Detractors (0-6)</b>
      </Text>
    ) : (
      <Text>
        2. Followup question for <b>Dissatisfied (1-2)</b>
      </Text>
    );

  return (
    <Flex className="compose" width={600} direction="column">
      <Flex className="compose__name-label" justifyContent="space-between" m="24px 0 8px">
        <Heading fontWeight="bold" className="settings__header" size="md" mb={2}>
          {surveyType} Survey Name
        </Heading>
      </Flex>
      <TextInput className="compose__name-input" value={composeSettings.surveyName} onChange={handleSurveyNameChange} />
      <Flex className="compose__intro-label" justifyContent="space-between" m="24px 0 8px">
        <Heading fontWeight="bold" className="settings__header" size="md" mb={2}>
          Introduction Message
        </Heading>
      </Flex>
      <WildcardTextInput
        text={composeSettings.introMessage}
        placeholder="Enter your message"
        type={WildcardInputQuestionTypes.Intro}
        showPreview={showPreview}
        textAreaRef={introMessageRef}
        allowFillInTheBlanks={false}
        handleChange={handleIntroMessageChange}
        setShowPreview={setShowPreview}
        validate={validate}
        errorMessage={getErrorMessage(composeSettings.introMessage)}
      />
      <WildCardButton
        textAreaRef={introMessageRef}
        stateHandler={handleAddWildCard}
        setShowPreview={setShowPreview}
        time
        petName
        clinicName
      />
      <Flex className="compose__questions" direction="column">
        <SurveyComposeQuestion
          titleComponent={<RatingQuestionTitle />}
          placeholder={`Edit ${surveyType} question`}
          value={composeSettings.ratingQuestion}
          type={WildcardInputQuestionTypes.Message}
          handleChange={handleRatingQuestionChange}
          textAreaRef={ratingQuestionRef}
          validate={validate}
          errorMessage={getErrorMessage(composeSettings.ratingQuestion)}
        >
          <RatingDots
            minValue={isNpsSurvey ? 0 : 1}
            maxValue={isNpsSurvey ? 10 : 5}
            centeredLabel={isNpsSurvey ? undefined : '1 - Poor, 2 - Fair, 3 - Satisfactory, 4 - Good, 5 - Excellent'}
            minLabel={isNpsSurvey ? 'Not Likely' : undefined}
            maxLabel={isNpsSurvey ? 'Very Likely' : undefined}
          />
        </SurveyComposeQuestion>
        <SurveyComposeQuestion
          titleComponent={<PositiveQuestionTitle />}
          placeholder="Enter a question"
          value={composeSettings.positiveQuestion}
          type={WildcardInputQuestionTypes.Question}
          handleChange={handlePositiveQuestionChange}
          inputRef={positiveQuestionRef}
          validate={validate}
          errorMessage={getErrorMessage(composeSettings.positiveQuestion)}
        >
          <StatusChangeSelect
            selectedStatusId={composeSettings.positiveStatusId}
            handleChange={handlePositiveStatusChange}
          />
        </SurveyComposeQuestion>
        <SurveyComposeQuestion
          titleComponent={<NegativeQuestionTitle />}
          placeholder="Enter a question"
          value={composeSettings.negativeQuestion}
          type={WildcardInputQuestionTypes.Question}
          handleChange={handleNegativeQuestionChange}
          inputRef={negativeQuestionRef}
          validate={validate}
          errorMessage={getErrorMessage(composeSettings.negativeQuestion)}
        >
          <StatusChangeSelect
            selectedStatusId={composeSettings.negativeStatusId}
            handleChange={handleNegativeStatusChange}
          />
        </SurveyComposeQuestion>
      </Flex>
    </Flex>
  );
};

export default SurveyCompose;
